import { toLower, trim } from 'lodash';
import type { ArgumentAction } from 'redux-logic/definitions/action';

import type { AuthUser, SwitchUserContext } from './types';

export enum AuthActionType {
    CHANGE_PASS = 'CHANGE_PASS',
    CHANGE_PASS_SUCCESS = 'CHANGE_PASS_SUCCESS',
    CHANGE_PASS_FAILURE = 'CHANGE_PASS_FAILURE',
    CREATE_PASSWORD = 'CREATE_PASSWORD',
    CREATE_PASS_SUCCESS = 'CREATE_PASS_SUCCESS',
    CREATE_PASS_FAILURE = 'CREATE_PASS_FAILURE',
    LOGIN_REQUEST = 'LOGIN_REQUEST',
    SWITCH_LOGIN_REQUEST = 'SWITCH_LOGIN_REQUEST',
    FINISH_SWITCH_USER = 'FINISH_SWITCH_USER',
    LOGIN_SUCCESS = 'LOGIN_SUCCESS',
    LOGIN_FAILURE = 'LOGIN_FAILURE',
    LOGOUT_REQUEST = 'LOGOUT_REQUEST',
    LOGOUT_SUCCESS = 'LOGOUT_SUCCESS',
    RESET_USER_DATA = 'RESET_USER_DATA',
    POLICY_REQUEST = 'POLICY_REQUEST',
    POLICY_SUCCESS = 'POLICY_SUCCESS',
    POLICY_FAILURE = 'POLICY_FAILURE',
    CLEAR_ERROR = 'CLEAR_ERROR',
    UPDATE_ACTIVE_STUDY_SITE = 'UPDATE_ACTIVE_STUDY_SITE',
    RESET_ACTIVE_STUDY_SITE = 'RESET_ACTIVE_STUDY_SITE',
    RESET_COMPLETE = 'RESET_COMPLETE',
    HANDLE_SWITCH_USER = 'HANDLE_SWITCH_USER',
    HANDLE_SWITCH_USER_TEMP_USER = 'HANDLE_SWITCH_USER_TEMP_USER',
    SWITCH_USER_FAILED_STATUS = 'SWITCH_USER_FAILED_STATUS',
    HANDLE_SWITCH_USER_CONTEXT = 'HANDLE_SWITCH_USER_CONTEXT',
    HANDLE_PASSWORD_CHANGE_STATUS = 'HANDLE_PASSWORD_CHANGE_STATUS',
    USER_CHANGE_PASS = 'USER_CHANGE_PASS',
    USER_CHANGE_PASS_SUCCESS = 'USER_CHANGE_PASS_SUCCESS',
    USER_CHANGE_PASS_FAILURE = 'USER_CHANGE_PASS_FAILURE',
    USER_UPDATE_SECURITY_QUESTIONS = 'USER_UPDATE_SECURITY_QUESTIONS',
    TELEVISIT_REQUEST = 'TELEVISIT_REQUEST',
    TELEVISIT_THUMBNAIL_MODE = 'TELEVISIT_THUMBNAIL_MODE',
    UPDATE_PATIENT_STUDY_ID = 'UPDATE_PATIENT_STUDY_ID',
    UPDATE_PATIENT_ARM_ID = 'UPDATE_PATIENT_ARM_ID',
    ACTIVATION_PASSWORD_REQUEST = 'ACTIVATION_PASSWORD_REQUEST',
    SET_SSO_LOADING = 'SET_SSO_LOADING',
    USER_ROLE_SWITCH_PENDING = 'USER_ROLE_SWITCH_PENDING',
}

export const AuthActions = {
    changePasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    createPasswordSucceeded: (authUser, newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASS_SUCCESS,
        payload: { authUser, newPassword },
    }),
    createPasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    loginRequest: (username, password, ssoTokens?): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_REQUEST,
        payload: { username: toLower(trim(username)), password: trim(password), ssoTokens },
    }),
    switchUserLoginRequest: (username, password, ssoTokens?): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SWITCH_LOGIN_REQUEST,
        payload: { username: toLower(trim(username)), password: trim(password), ssoTokens },
    }),
    finishSwitchUser: (authUser): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.FINISH_SWITCH_USER,
        payload: { authUser },
    }),
    loginSucceeded: (authUser): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_SUCCESS,
        payload: authUser,
    }),
    loginFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGIN_FAILURE,
        payload: err,
        error: true,
    }),
    logoutRequest: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGOUT_REQUEST,
    }),
    logoutSucceeded: (payload): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.LOGOUT_SUCCESS,
        payload,
    }),
    resetUserData: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_USER_DATA,
    }),
    policyRequest: (userData): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_REQUEST,
        payload: userData,
    }),
    policySucceeded: (userData): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_SUCCESS,
        payload: userData,
    }),
    policyFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.POLICY_FAILURE,
        payload: err,
        error: true,
    }),
    updateActiveStudySite: (payload: {
        study?;
        site?;
        updatedUser?;
    }): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_ACTIVE_STUDY_SITE,
        payload,
    }),
    resetActiveStudySite: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_ACTIVE_STUDY_SITE,
    }),
    resetComplete: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.RESET_COMPLETE,
    }),
    handleSwitchUser: (
        switchState,
        switchUserContext: SwitchUserContext = {
            status: 'IDLE',
            currentRespondentID: null,
            activationStatus: false,
            respondentPK: null,
            activationCode: null,
            activityCompletionStatus: false,
            caregivers: [],
            isCaregiverUser: false,
            hasLAR: false,
        }
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.HANDLE_SWITCH_USER,
        payload: switchState,
        meta: switchUserContext,
    }),
    switchUserFailedStatus: (
        attemptedUserType,
        error = { error: null }
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SWITCH_USER_FAILED_STATUS,
        payload: attemptedUserType,
        meta: error,
    }),
    handleSwitchUserContext: (
        updateSwitchUserContext: SwitchUserContext
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.HANDLE_SWITCH_USER_CONTEXT,
        payload: updateSwitchUserContext,
    }),
    userChangePasswordRequest: (oldPassword, newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS,
        payload: { oldPassword: trim(oldPassword), newPassword: trim(newPassword) },
    }),
    userChangePasswordSucceeded: (
        authUser: AuthUser,
        oldPassword: string,
        newPassword: string
    ): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS_SUCCESS,
        payload: {
            authUser,
            oldPassword,
            newPassword,
        },
    }),
    userChangePasswordFailed: (err): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_CHANGE_PASS_FAILURE,
        payload: err,
        error: true,
    }),
    userUpdateSecurityQuestions: (questions): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_UPDATE_SECURITY_QUESTIONS,
        payload: questions,
    }),
    updateTelevisitStatus: (data, active: boolean = false): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.TELEVISIT_REQUEST,
        payload: {
            data,
            active,
        },
    }),
    updateTelevisitThumbnailMode: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.TELEVISIT_THUMBNAIL_MODE,
    }),
    updatePatientStudyId: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_PATIENT_STUDY_ID,
        payload: {
            data,
        },
    }),
    updatePatientArmId: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.UPDATE_PATIENT_ARM_ID,
        payload: {
            data,
        },
    }),
    isNewPasswordRequested: (data): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.ACTIVATION_PASSWORD_REQUEST,
        payload: {
            data,
        },
    }),
    setSSOLoading: (data: boolean): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.SET_SSO_LOADING,
        payload: {
            data,
        },
    }),
    updateUserRoleSwitchPending: (isPending: boolean): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.USER_ROLE_SWITCH_PENDING,
        payload: {
            isPending,
        },
    }),
    // Used from connect in update_password_form.tsx
    changePasswordRequest: (newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS,
        payload: { newPassword: trim(newPassword) },
    }),
    // Used from connect in create_password_form.tsx
    createPasswordRequest: (newPassword): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CREATE_PASSWORD,
        payload: { newPassword: trim(newPassword) },
    }),
    // Used from connect in login.page.tsx
    handleSwitchUserTempUser: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.HANDLE_SWITCH_USER_TEMP_USER,
    }),
    changePasswordSucceeded: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CHANGE_PASS_SUCCESS,
    }),
    clearError: (): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.CLEAR_ERROR,
        error: false,
    }),
    // Used in patientApp/general.tsx and in user/general.tsx
    handlePasswordChangeStatus: (status): ArgumentAction<string, undefined, undefined> => ({
        type: AuthActionType.HANDLE_PASSWORD_CHANGE_STATUS,
        payload: status,
    }),
};
